// chat.js: Integración de voz + chat visual con respuesta hablada TUTORIA

import axios from 'axios';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { chatgpt_logo } from '../assets';
import ChatItem from '../components/ChatItems';
import { ASSISTANT_CHAT_HISTORY_URL, ASSISTANT_CHAT_SEND_URL } from '../constants';
import { AuthContext } from '../context/AuthContext';
import { ChatContext } from '../context/ChatContext';
import { sanitizeForDisplay } from '../utils/textUtils.js';

const Chat = ({ time }) => {
  // Contextos
  const { chatMessage, setChatMessage, setChatShow, setShowChatTime, setChatMenu, threadId, setThreadId } = useContext(ChatContext);
  const { user, course } = useContext(AuthContext);

  // Estados del componente
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [responses, setResponses] = useState([]);
  const [chatPower, setChatPower] = useState([]);
  const [files, setFiles] = useState([]);
  const [listening, setListening] = useState(false);
  const [speechEnabled] = useState(false); // (oculto, default off)
  const [socraticMode] = useState(false);  // (oculto, default off)
  const socraticPrompt = "A partir de ahora actúa como un tutor socrático. Haz preguntas en vez de dar respuestas directas. Guía al alumno con razonamiento paso a paso.";

  // Refs
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  const focusTextarea = () => setTimeout(() => textareaRef.current?.focus(), 0);

  // Cargar historial del chat
  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: user.IDalumno },
        });
        setChatPower(response.data.chatHistory);
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      } finally {
        focusTextarea();
      }
    };
    if (user?.IDalumno) {
      fetchChatHistory();
    }
  }, [user]);

  // Scroll automático cuando hay nuevas respuestas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [responses, chatPower]);

  // Al cambiar conversación
  useEffect(() => {
    setResponses([]);
    setMessage('');
    setFiles([]);
    adjustTextareaHeight();
    focusTextarea();
  }, [chatMessage]);

  // Reproduce texto por voz (silenciado por default)
  const speakText = (text) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    if (!speechEnabled) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'es-MX';
    synth.speak(utterance);
  };

  // Enviar mensaje al servidor y manejar respuesta
  const handleSend = async (forcedMessage) => {
    const finalMessage = forcedMessage || message;
    if (!finalMessage.trim() && !files.length) return;

    console.log('🧵 threadId (saliente):', threadId || '(none)');
    console.log('📦 Archivos seleccionados antes de enviar:', files.map((f) => f.name));

    const now = new Date();
    const filenames = files.map((file) => file.name);

    // Mostrar pregunta temporal
    const tempResponse = {
      message: finalMessage,
      response: 'Procesando respuesta...',
      filenames,
      timestamp: now.toISOString(),
      pending: true,
    };
    setResponses((prev) => [...prev, tempResponse]);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('userId', user.IDalumno);
      formData.append('time', time);

      const fullMessage = (socraticMode ? socraticPrompt + "\n\n" : "") + finalMessage + " " + (course?.["NOMBRELARGOCURSO"] || "");
      formData.append('message', fullMessage);

      // ✅ SOLO adjuntar threadId si es válido tipo "thread_..."
      if (threadId && /^thread_/.test(threadId)) {
        formData.append('threadId', threadId);
      }

      files.forEach((file) => formData.append("files", file));

      const res = await axios.post(ASSISTANT_CHAT_SEND_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      const cleanText = sanitizeForDisplay(res.data.answer);

      // Reemplazar la respuesta temporal
      setResponses((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          response: res.data.answer,
          filenames: res.data.filenames || [],
          pending: false,
        };
        return updated;
      });

      speakText(cleanText);

      setMessage('');
      setFiles([]);
      if (res.data.threadId) {
        console.log('🧵 threadId (entrante backend):', res.data.threadId);
        setThreadId(res.data.threadId);
      } else {
        console.warn('⚠️ Backend no devolvió threadId');
      }
      if (res.data.sessionVectorStoreId) {
        console.log('📦 VS de sesión:', res.data.sessionVectorStoreId);
      }

      focusTextarea();
    } catch (err) {
      console.error('Error sending message:', err);
      setResponses((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          response: 'Error al procesar la respuesta.',
          pending: false,
        };
        return updated;
      });
      focusTextarea();
    } finally {
      if (chatMessage.length === 0) {
        try {
          const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
            params: { userid: user.IDalumno }
          });
          setChatMenu(response.data.menu.reverse());
        } catch (error) {
          console.error('Error fetching chat history:', error.message);
        }
      }
      setLoading(false);
      focusTextarea();
    }
  };

  // Inicia una conversación nueva
  const handleNewDialog = () => {
    setShowChatTime(new Date().toISOString());
    setChatMessage([]);
    setChatShow(true);
    setThreadId(null);

    // Limpieza inmediata
    setResponses([]);
    setFiles([]);
    setMessage('');
    focusTextarea();

    const fetchChatHistory = async () => {
      try {
        const response = await axios.get(ASSISTANT_CHAT_HISTORY_URL, {
          params: { userid: user.IDalumno }
        });
        setChatMenu(response.data.menu);
      } catch (error) {
        console.error('Error fetching chat history:', error.message);
      } finally {
        focusTextarea();
      }
    };
    fetchChatHistory();
  };

  // Cambios de entrada manual
  const handleInputChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  // Subir archivos adjuntos
  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
    focusTextarea();
  };

  // Eliminar archivo
  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    focusTextarea();
  };

  // Enviar con Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Ajuste visual del textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  };

  // Reconocimiento de voz Web Speech API (si llegas a activarlo, limpiamos files)
  const startVoiceInput = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      console.error('SpeechRecognition API no disponible en este navegador.');
      return;
    }
    const recognition = new Recognition();
    recognition.lang = 'es-MX';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setMessage(transcript);
      // ✅ Evitar arrastrar adjuntos con voz
      setFiles([]);
      handleSend(transcript);
      setListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.start();
  };

  const formatTimestamp = (timestamp) => moment(timestamp).format('MMMM Do YYYY, h:mm:ss a');

  // --- RENDER ---
  return (
    <>
      {/* Historial de mensajes */}
      <div className="flex flex-col flex-grow overflow-y-auto w-full mx-auto p-4 bg-white">
        <div className="flex-grow overflow-y-auto w-full mx-auto">
          {chatMessage.map((item, index) => (
            <ChatItem
              key={`history-${index}`}
              index={index}
              timestamp={formatTimestamp(item.timestamp)}
              title={item.question}
              content={item.answer}
              filenames={item.filenames}
              chatgpt_logo={chatgpt_logo}
            />
          ))}

          {responses.map((item, index) => (
            <ChatItem
              key={`response-${index}`}
              index={index + chatMessage.length}
              timestamp={formatTimestamp(item.timestamp)}
              title={item.message}
              content={item.response}
              filenames={item.filenames}
              chatgpt_logo={chatgpt_logo}
            />
          ))}

          {loading && (
            <div className="flex justify-center items-center py-4">
              <div className="loader">
                Cargando...
                <i className="fas fa-spinner fa-spin text-2xl text-gray-500"></i>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Botones inferiores */}
      <div className="w-4/5 bottom-0 bg-white flex items-center space-x-3 mx-auto text-black text-[15px]">
        <div className="flex-grow" />
        <div className="flex items-end space-x-3 flex-wrap gap-2">
          {/* Botón nuevo diálogo */}
          <button
            onClick={handleNewDialog}
            className="p-2 sm:p-3 bg-white-500 text-black"
          >
            <i className="fas fa-broom"></i> &nbsp; Nuevo diálogo
          </button>
        </div>
      </div>

      {/* Área de texto + archivos */}
      <div className="w-full p-4">
        <div className="w-full bottom-0 mb-3 bg-white p-3 sm:p-4 flex items-center space-x-3 border rounded-lg border-gray mx-auto">
          <div className="flex-grow">
            {loading ? (
              <div className="loader">
                <i className="fas fa-spinner fa-spin text-2xl text-gray-500"></i> &nbsp;
                Generar mensaje...
              </div>
            ) : (
              <>
                {/* Lista de archivos */}
                <div className={`flex space-x-2 ${files.length ? "mb-4" : ""}`}>
                  {files.map((file, index) => (
                    <div key={index} className="rounded-lg border p-2 whitespace-nowrap">
                      <i className="fa-regular fa-file p-2 bg-blue-500 me-2 rounded-lg text-white"></i>
                      {file.name}
                      <button onClick={() => removeFile(index)} className="ms-2 cursor-pointer text-xs">
                        ❌
                      </button>
                    </div>
                  ))}
                </div>

                {/* Textarea */}
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="w-full bg-white focus:outline-none focus:border-white-400"
                  rows="1"
                  placeholder="Envíame un mensaje para ayudarte. Soy tu asistente virtual..."
                  style={{ maxHeight: '150px', overflow: 'auto' }}
                />
              </>
            )}
          </div>

          {/* Adjuntar / Enviar */}
          <div className="flex items-center space-x-3">
            {loading ? (
              <button className="p-2 bg-white-500 text-gray" disabled>
                <i className="fas fa-stop"></i>
              </button>
            ) : (
              <>
                {/* Botón para adjuntar archivos */}
                <label htmlFor="file-upload" className="cursor-pointer" title="Adjuntar archivos">
                  <i className="fas fa-paperclip text-xl p-2"></i>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  // accept=".pdf,.doc,.docx,.txt" // si quieres limitar
                />
                <button
                  onClick={() => handleSend()}
                  className="p-2 bg-white-500 text-black"
                  title="Enviar"
                >
                  <i className="fas fa-paper-plane"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Chat;
